/**
 * Functional optics library (Lens, Prism, Isomorphism).
 * Vendored from @atomic-object/lenses 0.1.6, which pulled in knex/minimist
 * transitively via @atomic-object/records (unused). This copy has no external
 * dependencies.
 */

import * as Arrays from './arrays';
export { Arrays };

export interface ILens<T, V> {
  get(t: T): V;
  set(a: T, value: V): T;
}

export interface Lens<T, V> {
  (t: T): V;
  get(t: T): V;
  set(v: V): (t: T) => T;
  set(t: T, v: V): T;
  update(fn: (v: V) => V): (t: T) => T;
  update(t: T, fn: (v: V) => V): T;
  comp<V2>(l: Lens<V, V2>): Lens<T, V2>;
  comp<U, V2>(l1: Lens<V, U>, l2: Lens<U, V2>): Lens<T, V2>;
  comp<U1, U2, V2>(
    l1: Lens<V, U1>, l2: Lens<U1, U2>, l3: Lens<U2, V2>
  ): Lens<T, V2>;
}

export interface IPrism<T, V> {
  get(t: T): V | undefined;
  set(a: T, value: V): T;
}

export interface Prism<T, V> {
  (t: T): V | undefined;
  get(t: T): V | undefined;
  set(v: V): (t: T) => T;
  set(t: T, v: V): T;
  update(fn: (v: V | undefined) => V | undefined): (t: T) => T;
  update(t: T, fn: (v: V | undefined) => V | undefined): T;
}

export type Isomorphism<T, V> = {
  to: (t: T) => V;
  from: (v: V) => T;
};

type Prismish<T, U> = ILens<T, U> | IPrism<T, U>;

function makeComposedSetter() {
  const performComposedSet = (
    o: any, v: any, lenses: Prismish<any, any>[], index: number
  ): any => {
    if (index === lenses.length - 1) {
      return lenses[index].set(o, v);
    } else {
      const inner = lenses[index].get(o);
      if (inner) {
        return lenses[index].set(
          o, performComposedSet(inner, v, lenses, index + 1)
        );
      } else {
        return o;
      }
    }
  };
  return performComposedSet;
}

function compImpl(...prisms: Prismish<any, any>[]): Prism<any, any> {
  let performComposedSet: ReturnType<typeof makeComposedSetter>;
  return Prism.of({
    get(o: any) {
      return prisms.reduce(
        (o, l) => (o === undefined ? o : l.get(o)), o
      );
    },
    set(o: any, v: any) {
      if (performComposedSet === undefined) {
        performComposedSet = makeComposedSetter();
      }
      return performComposedSet(o, v, prisms, 0);
    }
  });
}

export namespace Prism {
  export type Prismish<T, U> = ILens<T, U> | IPrism<T, U>;

  export function of<T, V>(spec: IPrism<T, V>): Prism<T, V> {
    const func = function(o: T) {
      return spec.get(o);
    } as Prism<T, V>;

    const set = function(tOrV: T | V, v?: V) {
      if (arguments.length === 1) {
        return function(t: T) { return spec.set(t, tOrV as V); };
      } else {
        return spec.set(tOrV as T, v!);
      }
    };

    const update = function(
      tOrFn: T | ((v: V | undefined) => V | undefined),
      f?: (v: V | undefined) => V | undefined
    ) {
      if (f === undefined) {
        return function(t: T) {
          return func.update(
            t, tOrFn as (v: V | undefined) => V | undefined
          );
        };
      } else {
        const t = tOrFn as T;
        const v = spec.get(t);
        if (v === undefined) {
          return t;
        } else {
          return spec.set(t, f(v)!);
        }
      }
    };

    const instanceComp = function(
      ...prisms: Prismish<any, any>[]
    ): Prism<any, any> {
      return compImpl(func, ...prisms);
    };

    func.get = spec.get;
    func.set = set as Prism<T, V>['set'];
    func.update = update as Prism<T, V>['update'];
    (func as any).comp = instanceComp;
    return func;
  }

  export function comp<T, U, V>(
    l1: Prismish<T, U>, l2: Prismish<U, V>
  ): Prism<T, V>;
  export function comp<T, U1, U2, V>(
    l1: Prismish<T, U1>, l2: Prismish<U1, U2>, l3: Prismish<U2, V>
  ): Prism<T, V>;
  export function comp<T, U1, U2, U3, V>(
    l1: Prismish<T, U1>, l2: Prismish<U1, U2>,
    l3: Prismish<U2, U3>, l4: Prismish<U3, V>
  ): Prism<T, V>;
  export function comp<T, U1, U2, U3, U4, V>(
    l1: Prismish<T, U1>, l2: Prismish<U1, U2>,
    l3: Prismish<U2, U3>, l4: Prismish<U3, U4>, l5: Prismish<U4, V>
  ): Prism<T, V>;
  export function comp(
    ...prisms: Prismish<any, any>[]
  ): Prism<any, any> {
    return compImpl(...prisms);
  }
}

export namespace Lens {
  export function from<T>(): LensFactory<T> {
    return new LensFactory<T>();
  }

  export function of<T, V>(spec: ILens<T, V>): Lens<T, V> {
    return Prism.of(spec) as Lens<T, V>;
  }

  export function map<T, U, V>(
    l: ILens<T, U>, f: Isomorphism<U, V>
  ): Lens<T, V> {
    return Lens.of({
      get: (o: T) => f.to(l.get(o)),
      set: (o: T, v: V) => l.set(o, f.from(v))
    });
  }

  export function comp<T, U, V>(
    l1: ILens<T, U>, l2: ILens<U, V>
  ): Lens<T, V>;
  export function comp<T, U1, U2, V>(
    l1: ILens<T, U1>, l2: ILens<U1, U2>, l3: ILens<U2, V>
  ): Lens<T, V>;
  export function comp<T, U1, U2, U3, V>(
    l1: ILens<T, U1>, l2: ILens<U1, U2>,
    l3: ILens<U2, U3>, l4: ILens<U3, V>
  ): Lens<T, V>;
  export function comp<T, U1, U2, U3, U4, V>(
    l1: ILens<T, U1>, l2: ILens<U1, U2>,
    l3: ILens<U2, U3>, l4: ILens<U3, U4>, l5: ILens<U4, V>
  ): Lens<T, V>;
  export function comp(
    ...lenses: ILens<any, any>[]
  ): Lens<any, any> {
    return compImpl(...lenses) as Lens<any, any>;
  }
}

function makeUpdater() {
  const performUpdate = (
    o: any, v: any, keys: string[], idx: number, last: number
  ): any => {
    const copy = { ...o };
    if (idx === last) {
      copy[keys[idx]] = v;
      return copy;
    } else {
      copy[keys[idx]] = performUpdate(
        o[keys[idx]], v, keys, idx + 1, last
      );
      return copy;
    }
  };
  return performUpdate;
}

export interface SafeUpdate {
  <O, K1 extends keyof O>(o: O, k: K1, v: O[K1]): O;
  <O, K1 extends keyof O, K2 extends keyof O[K1]>(
    o: O, k: K1, k2: K2, v: O[K1][K2]
  ): O;
  <O, K1 extends keyof O, K2 extends keyof O[K1],
   K3 extends keyof O[K1][K2]>(
    o: O, k: K1, k2: K2, k3: K3, v: O[K1][K2][K3]
  ): O;
  <O, K1 extends keyof O, K2 extends keyof O[K1],
   K3 extends keyof O[K1][K2], K4 extends keyof O[K1][K2][K3]>(
    o: O, k: K1, k2: K2, k3: K3, k4: K4, v: O[K1][K2][K3][K4]
  ): O;
  <O, K1 extends keyof O, K2 extends keyof O[K1],
   K3 extends keyof O[K1][K2], K4 extends keyof O[K1][K2][K3],
   K5 extends keyof O[K1][K2][K3][K4]>(
    o: O, k: K1, k2: K2, k3: K3, k4: K4, k5: K5,
    v: O[K1][K2][K3][K4][K5]
  ): O;
}

const _generalUpdater = makeUpdater();
export const updateIn: SafeUpdate = function(
  o: any, ...args: any[]
) {
  return _generalUpdater(
    o, args[args.length - 1], args, 0, args.length - 2
  );
} as SafeUpdate;

export class LensFactory<O> {
  prop<K extends keyof O>(k: K): Lens<O, O[K]>;
  prop<K extends keyof O, K2 extends keyof O[K]>(
    k: K, k2: K2
  ): Lens<O, O[K][K2]>;
  prop<K extends keyof O, K2 extends keyof O[K],
   K3 extends keyof O[K][K2]>(
    k: K, k2: K2, k3: K3
  ): Lens<O, O[K][K2][K3]>;
  prop<K extends keyof O, K2 extends keyof O[K],
   K3 extends keyof O[K][K2], K4 extends keyof O[K][K2][K3]>(
    k: K, k2: K2, k3: K3, k4: K4
  ): Lens<O, O[K][K2][K3][K4]>;
  prop(...ks: string[]): Lens<O, any> {
    let performUpdate: ReturnType<typeof makeUpdater>;
    return Lens.of({
      get: (o: O) => ks.reduce((x: any, k) => x[k], o as any),
      set: (o: O, v: any) => {
        if (performUpdate === undefined) {
          performUpdate = makeUpdater();
        }
        return performUpdate(o, v, ks, 0, ks.length - 1);
      }
    });
  }
}
